import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import { Plus, X } from 'lucide-react'
import type { AttentionLevel, Tag } from '@/src/renderer/lib/db/models'
import { TaskAttentionIcon, TaskStartIcon, TaskDeadlineIcon, DurationIcon, LightbulbIcon } from '@/src/renderer/components/icons'
import { formatDuration } from '../../utils/parseDuration'
import { formatStartBadge, formatStartDate } from '../../utils/parseStartDate'
import { formatDeadlineDate, formatDeadlineBadge } from '../DeadlineDropdown/deadlineDropdownUtils'
import { TaskDropdown } from '../TaskDropdown'
import { AttentionDropdownContent } from '../AttentionDropdown'
import { WhenDropdownContent, TodayIcon, TomorrowIcon, PeriodIcon } from '../WhenDropdown'
import { DeadlineDropdownContent } from '../DeadlineDropdown'
import { DurationDropdownContent } from '../DurationDropdown'
import { TagDropdownContent } from '../TagDropdown'
import { TaskActionBar } from '../TaskActionBar'
import { useTags } from '../../hooks/useTags'
import { useBadgeHover, BADGE_HOVER_DELAY } from './hooks/useBadgeHover'
import type { DurationValue } from './TaskEntry.types'
import type { StartValue } from '../WhenDropdown'
import type { DeadlineValue } from '../DeadlineDropdown'

interface ExpandedBottomBarProps {
  attention: AttentionLevel | null
  start: StartValue | null
  deadline: DeadlineValue | null
  duration: DurationValue | null
  tags: string[]
  isStartToday: boolean
  isStartTomorrow: boolean
  isStartOverdue: boolean
  isDeadlineToday: boolean
  isDeadlineTomorrow: boolean
  isDeadlineOverdue: boolean
  handleAttentionChange: (value: AttentionLevel | null) => void
  handleStartChange: (value: StartValue | null) => void
  handleDeadlineChange: (value: DeadlineValue | null) => void
  handleDurationChange: (value: DurationValue | null) => void
  handleTagsChange: (value: string[]) => void
  playSoftRemove: () => void
}

export const ExpandedBottomBar: React.FC<ExpandedBottomBarProps> = ({
  attention, start, deadline, duration, tags,
  isStartToday, isStartTomorrow, isStartOverdue, isDeadlineToday, isDeadlineTomorrow, isDeadlineOverdue,
  handleAttentionChange, handleStartChange, handleDeadlineChange,
  handleDurationChange, handleTagsChange, playSoftRemove,
}) => {
  const { i18n } = useTranslation()
  const { tags: allTags } = useTags()

  // ── Badge dropdown states ──
  const [badgeAttentionOpen, setBadgeAttentionOpen] = useState(false)
  const [badgeStartOpen, setBadgeStartOpen] = useState(false)
  const [badgeDeadlineOpen, setBadgeDeadlineOpen] = useState(false)
  const [badgeDurationOpen, setBadgeDurationOpen] = useState(false)
  const [badgeTagsOpen, setBadgeTagsOpen] = useState(false)
  const [hoveredTagId, setHoveredTagId] = useState<string | null>(null)
  const [activeTagId, setActiveTagId] = useState<string | null>(null)

  // ── Badge hover state ──
  const attentionHover = useBadgeHover(attention)
  const startHover = useBadgeHover(start)
  const deadlineHover = useBadgeHover(deadline)
  const durationHover = useBadgeHover(duration)
  const tagHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Close all other badge dropdowns when switching to a new one
  const closeOtherBadges = useCallback((except: string) => {
    if (except !== 'attention') setBadgeAttentionOpen(false)
    if (except !== 'start') { setBadgeStartOpen(false); startHover.setIsHovered(false) }
    if (except !== 'deadline') { setBadgeDeadlineOpen(false); deadlineHover.setIsHovered(false) }
    if (except !== 'duration') setBadgeDurationOpen(false)
    if (except !== 'tags') { setBadgeTagsOpen(false); setActiveTagId(null); setHoveredTagId(null) }
  }, [startHover, deadlineHover])

  const anyBadgeOpen = badgeAttentionOpen || badgeStartOpen || badgeDeadlineOpen || badgeDurationOpen || badgeTagsOpen

  return (
    <>
      <div className="task-entry__assigned-props">
        <LayoutGroup>
        <AnimatePresence>
          {attention && (
            <motion.div
              key="priority-badge"
              layout="position"
              className="task-entry__priority-badge-motion task-entry__priority-badge-motion--attention"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7, width: 0 }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
            >
              <div
                className={`task-entry__priority-badge${attentionHover.isHovered && !badgeAttentionOpen ? ' task-entry__priority-badge--expanded' : ''}${badgeAttentionOpen ? ' task-entry__priority-badge--open' : ''}`}
                onMouseEnter={() => {
                  if (anyBadgeOpen) { closeOtherBadges('attention'); setBadgeAttentionOpen(true); return }
                  attentionHover.onMouseEnter()
                }}
                onMouseLeave={attentionHover.onMouseLeave}
              >
                <TaskDropdown
                  open={badgeAttentionOpen}
                  onOpenChange={setBadgeAttentionOpen}
                  side="bottom"
                  align="start"
                  contentStyle={{ width: 220 }}
                  trigger={
                    <button className="task-entry__priority-badge-trigger" type="button">
                      <AnimatePresence mode="popLayout" initial={false}>
                        <motion.span
                          key={attention}
                          className="task-entry__priority-badge-icon"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.32, ease: 'easeInOut' }}
                        >
                          <TaskAttentionIcon size={16} level={attention} />
                        </motion.span>
                      </AnimatePresence>
                    </button>
                  }
                >
                  <AttentionDropdownContent
                    value={attention}
                    onChange={handleAttentionChange}
                    onClose={() => { setBadgeAttentionOpen(false); attentionHover.setIsHovered(false) }}
                    onRemove={() => { playSoftRemove(); handleAttentionChange(null); setBadgeAttentionOpen(false); attentionHover.setIsHovered(false) }}
                    variant="badge"
                  />
                </TaskDropdown>
                <AnimatePresence>
                  {(attentionHover.isHovered && !badgeAttentionOpen) && (
                    <motion.button
                      key="remove"
                      className="task-entry__priority-remove"
                      type="button"
                      initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                      animate={{ opacity: 1, width: 20, marginLeft: 2 }}
                      exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                      onMouseUp={() => { playSoftRemove(); handleAttentionChange(null) }}
                    >
                      <X size={12} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Start date badge */}
        <AnimatePresence>
          {start && (
            <motion.div
              key="start-badge"
              layout="position"
              className="task-entry__priority-badge-motion task-entry__priority-badge-motion--start"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7, width: 0 }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
            >
              <div
                className={`task-entry__duration-badge${isStartOverdue ? ' task-entry__duration-badge--start-overdue' : ''}${startHover.isHovered && !badgeStartOpen ? ' task-entry__duration-badge--expanded' : ''}${badgeStartOpen ? ' task-entry__duration-badge--open' : ''}`}
                onMouseEnter={() => {
                  if (anyBadgeOpen) { closeOtherBadges('start'); setBadgeStartOpen(true); return }
                  startHover.onMouseEnter()
                }}
                onMouseLeave={startHover.onMouseLeave}
              >
                <TaskDropdown
                  open={badgeStartOpen}
                  onOpenChange={setBadgeStartOpen}
                  side="bottom"
                  align="start"
                  disableResize
                  trigger={
                    <button className="task-entry__duration-badge-trigger" type="button">
                      {start.period && (isStartToday || isStartTomorrow) ? <PeriodIcon period={start.period} size={16} />
                        : isStartToday ? <TodayIcon size={16} />
                        : isStartTomorrow ? <TomorrowIcon size={16} />
                        : <TaskStartIcon size={16} />
                      }
                      <span className="task-entry__duration-badge-text">
                        {start.period || start.time
                          ? formatStartDate(start, i18n.language)
                          : formatStartBadge(start, i18n.language)
                        }
                      </span>
                    </button>
                  }
                >
                  <WhenDropdownContent
                    value={start}
                    onChange={handleStartChange}
                    onClose={() => { setBadgeStartOpen(false); startHover.setIsHovered(false) }}
                    onRemove={() => { playSoftRemove(); handleStartChange(null); setBadgeStartOpen(false); startHover.setIsHovered(false) }}
                    variant="badge"
                  />
                </TaskDropdown>
                <AnimatePresence>
                  {(startHover.isHovered && !badgeStartOpen) && (
                    <motion.button
                      key="start-remove"
                      className="task-entry__priority-remove"
                      type="button"
                      initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                      animate={{ opacity: 1, width: 20, marginLeft: 2 }}
                      exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                      onMouseUp={() => { playSoftRemove(); handleStartChange(null) }}
                    >
                      <X size={12} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Duration badge */}
        <AnimatePresence>
          {duration && (
            <motion.div
              key="duration-badge"
              layout="position"
              className="task-entry__priority-badge-motion"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7, width: 0 }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
            >
              <div
                className={`task-entry__duration-badge${durationHover.isHovered && !badgeDurationOpen ? ' task-entry__duration-badge--expanded' : ''}${badgeDurationOpen ? ' task-entry__duration-badge--open' : ''}`}
                onMouseEnter={() => {
                  if (anyBadgeOpen) { closeOtherBadges('duration'); setBadgeDurationOpen(true); return }
                  durationHover.onMouseEnter()
                }}
                onMouseLeave={durationHover.onMouseLeave}
              >
                <TaskDropdown
                  open={badgeDurationOpen}
                  onOpenChange={setBadgeDurationOpen}
                  side="bottom"
                  align="start"
                  trigger={
                    <button className="task-entry__duration-badge-trigger" type="button">
                      {duration.mode === 'thinking' ? <LightbulbIcon size={16} /> : <DurationIcon size={16} />}
                      <span className="task-entry__duration-badge-text">
                        {formatDuration(duration.minutes)}
                      </span>
                    </button>
                  }
                >
                  <DurationDropdownContent
                    value={duration}
                    onChange={handleDurationChange}
                    onClose={() => { setBadgeDurationOpen(false); durationHover.setIsHovered(false) }}
                    onRemove={() => { playSoftRemove(); handleDurationChange(null); setBadgeDurationOpen(false); durationHover.setIsHovered(false) }}
                    variant="badge"
                  />
                </TaskDropdown>
                <AnimatePresence>
                  {(durationHover.isHovered && !badgeDurationOpen) && (
                    <motion.button
                      key="duration-remove"
                      className="task-entry__priority-remove"
                      type="button"
                      initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                      animate={{ opacity: 1, width: 20, marginLeft: 2 }}
                      exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                      onMouseUp={() => { playSoftRemove(); handleDurationChange(null) }}
                    >
                      <X size={12} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Deadline badge */}
        <AnimatePresence>
          {deadline && (
            <motion.div
              key="deadline-badge"
              layout="position"
              className="task-entry__priority-badge-motion"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7, width: 0 }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
            >
              <div
                className={`task-entry__duration-badge${isDeadlineOverdue ? ' task-entry__duration-badge--deadline-overdue' : isDeadlineToday ? ' task-entry__duration-badge--deadline-today' : isDeadlineTomorrow ? ' task-entry__duration-badge--deadline-tomorrow' : ''}${deadlineHover.isHovered && !badgeDeadlineOpen ? ' task-entry__duration-badge--expanded' : ''}${badgeDeadlineOpen ? ' task-entry__duration-badge--open' : ''}`}
                onMouseEnter={() => {
                  if (anyBadgeOpen) { closeOtherBadges('deadline'); setBadgeDeadlineOpen(true); return }
                  deadlineHover.onMouseEnter()
                }}
                onMouseLeave={deadlineHover.onMouseLeave}
              >
                <TaskDropdown
                  open={badgeDeadlineOpen}
                  onOpenChange={setBadgeDeadlineOpen}
                  side="bottom"
                  disableResize
                  align="start"
                  trigger={
                    <button className="task-entry__duration-badge-trigger" type="button">
                      <TaskDeadlineIcon size={16} />
                      <span className="task-entry__duration-badge-text">
                        {deadline.period || deadline.time
                          ? formatDeadlineDate(deadline, i18n.language)
                          : formatDeadlineBadge(deadline, i18n.language)
                        }
                      </span>
                    </button>
                  }
                >
                  <DeadlineDropdownContent
                    value={deadline}
                    onChange={handleDeadlineChange}
                    onClose={() => { setBadgeDeadlineOpen(false); deadlineHover.setIsHovered(false) }}
                    onRemove={() => { playSoftRemove(); handleDeadlineChange(null); setBadgeDeadlineOpen(false); deadlineHover.setIsHovered(false) }}
                    variant="badge"
                  />
                </TaskDropdown>
                <AnimatePresence>
                  {(deadlineHover.isHovered && !badgeDeadlineOpen) && (
                    <motion.button
                      key="deadline-remove"
                      className="task-entry__priority-remove"
                      type="button"
                      initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                      animate={{ opacity: 1, width: 20, marginLeft: 2 }}
                      exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                      onMouseUp={() => { playSoftRemove(); handleDeadlineChange(null) }}
                    >
                      <X size={12} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tag pills + Plus button */}
        <AnimatePresence>
          {tags.length > 0 && (
            <motion.div
              key="tag-badges"
              layout="position"
              className="task-entry__priority-badge-motion"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7, width: 0 }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
            >
              <div className="task-entry__tag-badges">
                <AnimatePresence initial={false}>
                {tags.map(tagId => {
                  const tagObj = allTags.find(t => t.id === tagId)
                  if (!tagObj) return null
                  return (
                    <motion.div
                      key={tagId}
                      initial={{ opacity: 0, scale: 0.7, width: 0 }}
                      animate={{ opacity: 1, scale: 1, width: 'auto' }}
                      exit={{ opacity: 0, scale: 0.7, width: 0 }}
                      style={{ overflow: 'hidden' }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                    >
                    <div
                      className={`task-entry__tag-pill-wrapper${hoveredTagId === tagId && activeTagId !== tagId ? ' task-entry__tag-pill-wrapper--expanded' : ''}${badgeTagsOpen && activeTagId === tagId ? ' task-entry__tag-pill-wrapper--open' : ''}`}
                      onMouseEnter={() => {
                        if (anyBadgeOpen && !(badgeTagsOpen && activeTagId === tagId)) {
                          closeOtherBadges('tags')
                          setBadgeTagsOpen(true); setActiveTagId(tagId); setHoveredTagId(tagId)
                          return
                        }
                        if (badgeTagsOpen && activeTagId !== tagId) {
                          setActiveTagId(tagId); setHoveredTagId(tagId); return
                        }
                        tagHoverTimer.current = setTimeout(() => setHoveredTagId(tagId), BADGE_HOVER_DELAY)
                      }}
                      onMouseLeave={() => { if (tagHoverTimer.current) clearTimeout(tagHoverTimer.current); setHoveredTagId(null) }}
                    >
                      <TaskDropdown
                        open={badgeTagsOpen && activeTagId === tagId}
                        onOpenChange={(open) => {
                          setBadgeTagsOpen(open)
                          if (open) setActiveTagId(tagId)
                          else setActiveTagId(null)
                        }}
                        side="bottom"
                        align="start"
                        trigger={<button className="task-entry__tag-pill" type="button">{tagObj.name}</button>}
                      >
                        <TagDropdownContent
                          selectedIds={tags}
                          onChange={handleTagsChange}
                          onClose={() => { setBadgeTagsOpen(false); setActiveTagId(null); setHoveredTagId(null) }}
                          onRemoveAll={() => { tags.forEach((_, i) => setTimeout(() => playSoftRemove(), i * 80)); handleTagsChange([]); setBadgeTagsOpen(false); setActiveTagId(null); setHoveredTagId(null) }}
                          variant="badge"
                          highlightId={activeTagId}
                        />
                      </TaskDropdown>
                      <AnimatePresence>
                        {(hoveredTagId === tagId && !(badgeTagsOpen && activeTagId === tagId)) && (
                          <motion.button
                            key="tag-remove"
                            className="task-entry__priority-remove"
                            type="button"
                            initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                            animate={{ opacity: 1, width: 20, marginLeft: 0 }}
                            exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                            transition={{ duration: 0.15, ease: 'easeInOut' }}
                            onMouseUp={() => { playSoftRemove(); handleTagsChange(tags.filter(id => id !== tagId)) }}
                          >
                            <X size={12} />
                          </motion.button>
                        )}
                      </AnimatePresence>
                    </div>
                    </motion.div>
                  )
                })}
                </AnimatePresence>
                {/* Plus button */}
                <TaskDropdown
                  open={badgeTagsOpen && activeTagId === null}
                  onOpenChange={(open) => {
                    setBadgeTagsOpen(open)
                    if (open) setActiveTagId(null)
                  }}
                  side="bottom"
                  align="start"
                  trigger={
                    <button
                      className="task-entry__tag-pill task-entry__tag-pill--add"
                      type="button"
                      onMouseEnter={() => {
                        setHoveredTagId(null)
                        if (anyBadgeOpen) {
                          closeOtherBadges('tags')
                          setBadgeTagsOpen(true); setActiveTagId(null)
                          return
                        }
                        if (badgeTagsOpen && activeTagId !== null) {
                          setActiveTagId(null)
                        }
                      }}
                    >
                      <Plus size={12} />
                    </button>
                  }
                >
                  <TagDropdownContent
                    selectedIds={tags}
                    onChange={(ids) => handleTagsChange([...new Set([...tags, ...ids])])}
                    onClose={() => { setBadgeTagsOpen(false); setHoveredTagId(null) }}
                    variant="add"
                  />
                </TaskDropdown>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </LayoutGroup>
      </div>
      <TaskActionBar
        start={start} onStartChange={handleStartChange}
        deadline={deadline} onDeadlineChange={handleDeadlineChange}
        attention={attention} onAttentionChange={handleAttentionChange}
        duration={duration} onDurationChange={handleDurationChange}
        tags={tags} onTagsChange={handleTagsChange}
      />
    </>
  )
}
